import type { ListEligibleMembershipsResponse } from '@wix/ambassador-ecom-memberships-spi-host-v1-membership/types';
import type { ControllerFlowAPI, Experiments } from '@wix/yoshi-flow-editor';
import settingsParams from '../../../../components/BookingsForm/settingsParams';
import { ExperimentsConsts } from '../../../../consts/experiments';
import {
  PaymentMethod,
  ReservedPaymentOptionIds,
} from '../../../../types/types';
import type { ServicePayment } from '../../../mappers/serviceMapper/utils/payment.mapper';
import type { CoveringPurchasableBenefit } from '../../benefitCoverage/benefitCoverage';
import {
  buildBuyPlanAndBookOptionId,
  DepositOrFullAmountOptionIds,
  isDepositOrFullAmountFeatureEnabled,
  isOfferedAsOneTimeOnly,
  isOfferedAsPricingPlanOnly,
  isServiceHavePricingPlans,
} from '../utils';

export const getDefaultPaymentOptionId = ({
  settings,
  servicePayment,
  memberships,
  isPricingPlanInstalled,
  isDynamicPricingCustomOptions,
  hasEligiblePlans,
  experiments,
  paymentTypes,
  purchasableBenefits,
  excludePricingPlanOptions = false,
}: {
  settings: ControllerFlowAPI['settings'];
  servicePayment: ServicePayment;
  isPricingPlanInstalled: boolean;
  memberships?: ListEligibleMembershipsResponse;
  isDynamicPricingCustomOptions?: boolean;
  hasEligiblePlans?: boolean;
  experiments?: Experiments;
  paymentTypes?: PaymentMethod[];
  purchasableBenefits?: CoveringPurchasableBenefit[];
  excludePricingPlanOptions?: boolean;
}) => {
  // Plan & Book: where the legacy flow defaults to the generic "Buy a plan" entry,
  // default to the first concrete purchasable plan option instead.
  const buyPlanDefaultOptionId = () => {
    if (
      experiments?.enabled(ExperimentsConsts.PlanAndBook) &&
      purchasableBenefits?.length
    ) {
      const { plan, benefit } = purchasableBenefits[0];
      return buildBuyPlanAndBookOptionId(
        plan.catalogReference?.catalogItemId,
        benefit.benefitId,
      );
    }
    return ReservedPaymentOptionIds.BuyAPricingPlan;
  };

  const getDefaultSingleSessionPaymentOptionId = () =>
    isDepositOrFullAmountFeatureEnabled({
      experiments,
      fullUpfrontPaymentAllowed:
        servicePayment.paymentDetails.fullUpfrontPaymentAllowed,
      deposit: servicePayment.paymentDetails.minCharge,
      paymentTypes: paymentTypes ?? [],
    })
      ? DepositOrFullAmountOptionIds.Deposit
      : ReservedPaymentOptionIds.SingleSession;

  if (
    servicePayment.paymentDetails.subscriptionInfo &&
    experiments?.enabled(ExperimentsConsts.SubscriptionPricingUoU)
  ) {
    return ReservedPaymentOptionIds.Subscription;
  }

  if (isDynamicPricingCustomOptions) {
    return getDefaultSingleSessionPaymentOptionId();
  }
  const planId = excludePricingPlanOptions
    ? undefined
    : memberships?.eligibleMemberships?.[0]?.id; // todo: change to Selected Plan when implemented

  if (planId) {
    return planId;
  }
  if (
    excludePricingPlanOptions ||
    isOfferedAsOneTimeOnly(servicePayment) ||
    !isPricingPlanInstalled
  ) {
    return getDefaultSingleSessionPaymentOptionId();
  }
  if (isOfferedAsPricingPlanOnly(servicePayment)) {
    return buyPlanDefaultOptionId();
  }

  const selected =
    [
      PaymentMethod.SINGLE,
      PaymentMethod.ONLINE,
      PaymentMethod.OFFLINE,
    ].includes(settings.get(settingsParams.defaultPaymentMethod)) ||
    !isServiceHavePricingPlans(servicePayment) ||
    !hasEligiblePlans
      ? getDefaultSingleSessionPaymentOptionId()
      : buyPlanDefaultOptionId();
  return selected;
};
